import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  Box,
  Text,
  Tooltip,
  Button,
  Group,
  Stack,
  Card,
  ThemeIcon,
  Modal,
  LoadingOverlay,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { ReusableTable } from "../../Shared/table";
import { useNavigate } from "react-router-dom";
import FilterComponent from "../../Filter";
import SmartFhirLoadingScreen from "../../Shared/SmartFhirLoadingScreen";
import PaginationComponent from "../../Shared/Pagination";
import { transformApiDataToPatientQuery } from "../../../utils/data-transform-utils";
import { fetchHtmlUrlQuery, fetchPdfUrlQuery } from "../../../hooks/queries";
import { convertToSelectOptions } from "../../../utils";
import { initializeEncryptionIfNeeded } from "../../../utils/fhir-init-utils";
import {
  getHeldFhirClient,
  holdFhirClient,
  releaseFhirClient,
} from "../../../services/fhir-client-holder";
import { RouteConstants } from "../../../constants/route-constant";
import FHIR from "fhirclient";
import { statusColors } from "../../../constants/color-constants";
import {
  IconAlertCircle,
  IconRefresh,
  IconDatabase,
  IconWifi,
} from "@tabler/icons-react";
import { HtmlViewer } from "../../Shared/HtmlViewer";
import { useAppSelector, useAppDispatch } from "../../../store/hooks";
import { STORAGE_KEYS } from "../../../services/encrypt-decrypt";
import { setProviderNPI } from "../../../store/providerReducer";
import {
  setFhirInitializing,
  setFhirInitialized,
  setFhirFailed,
  setFhirMrn,
  setFHIRPatientAndPractitioner,
} from "../../../store/fhirReducer";
import { querySubmittedStates } from "../../../constants/app-constant";
import {
  useQueryList,
  useStatusList,
  useTopicList,
} from "../../../hooks/queries";
import { useQueryClient } from "@tanstack/react-query";

// Define the data type for patient query table
export interface PatientQueryData {
  id: string;
  topic: string;
  status: string;
  statusId: string | number; ////STATUS CHANGES
  ageing: number | string;
  queryBy: string;
  queryDate: string;
  patientId: string;
  isEditable: string;
  createdFormatTime: string;
  updatedFormatTime: string;
}

// PDF Response interface
interface PdfResponse {
  blobData: Blob;
  filename: string;
  contentType: string;
  size: number;
}

// FHIR Data interfaces
interface FhirPractitioner {
  id: string;
  name?: Array<{
    family?: string;
    given?: string[];
    text?: string;
  }>;
  identifier?: Array<{
    system?: string;
    value?: string;
    type?: {
      coding?: Array<{
        system?: string;
        code?: string;
        display?: string;
      }>;
      text?: string;
    };
  }>;
  telecom?: Array<{
    system?: string;
    value?: string;
    use?: string;
  }>;
  qualification?: Array<{
    code?: {
      coding?: Array<{
        system?: string;
        code?: string;
        display?: string;
      }>;
      text?: string;
    };
  }>;
}

const createCustomStatusOptions = (originalStatusOptions: any[]) => {
  const customStatusOptions = [
    { value: "open_and_in_progress", label: "Open & In-Progress" },
    { value: "open", label: "Open" }, // Added this
    { value: "in_progress", label: "In Progress" }, // Added this
    { value: "completed", label: "Completed" },
  ];

  // Define mapping for what actual status values each custom option represents
  // NOW MATCHES MyQueries exactly
  const statusMapping = {
    // Handle the combined "Open & In-Progress" status
    open_and_in_progress: originalStatusOptions
      ?.filter(
        (option) =>
          option.label.toLowerCase().includes("open") &&
          option.label.toLowerCase().includes("progress") &&
          option.label.toLowerCase().includes("&")
      )
      ?.map((option) => option.value),

    // Individual open statuses (NEW - matches MyQueries)
    open: originalStatusOptions
      ?.filter(
        (option) =>
          (option.label.toLowerCase().includes("open") ||
            option.label.toLowerCase().includes("new") ||
            option.label.toLowerCase().includes("pending")) &&
          !option.label.toLowerCase().includes("&") // Exclude the combined option
      )
      ?.map((option) => option.value),

    // Individual in-progress statuses (NEW - matches MyQueries)
    in_progress: originalStatusOptions
      ?.filter(
        (option) =>
          (option.label.toLowerCase().includes("progress") ||
            option.label.toLowerCase().includes("processing") ||
            option.label.toLowerCase().includes("reviewing")) &&
          !option.label.toLowerCase().includes("&") // Exclude the combined option
      )
      ?.map((option) => option.value),

    // Completed statuses (same as before)
    completed: originalStatusOptions
      ?.filter(
        (option) =>
          option.label.toLowerCase().includes("completed") ||
          option.label.toLowerCase().includes("agreed") ||
          option.label.toLowerCase().includes("disagreed") ||
          option.label.toLowerCase().includes("unable") ||
          option.label.toLowerCase().includes("other") || ////STATUS CHANGES
          option.label.toLowerCase().includes("alternate") ||
          option.label.toLowerCase().includes("answered")
      )
      ?.map((option) => option.value),
  };

  return { customStatusOptions, statusMapping };
};

// Helper function to convert custom status filter to actual status values
const convertCustomStatusToActual = (
  customStatusFilter: string[],
  statusMapping: any
) => {
  const actualStatusValues: string[] = [];

  customStatusFilter?.forEach((customStatus) => {
    if (statusMapping[customStatus]) {
      actualStatusValues?.push(...statusMapping[customStatus]);
    }
  });

  return actualStatusValues;
};

const limit = 15;

export default function PatientQuery() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const theme = useMantineTheme();
  const isSmallScreen = useMediaQuery(`(max-width: ${theme.breakpoints.md})`);
  const provider = useAppSelector((state) => state?.provider?.provider);
  const fhirState = useAppSelector((state) => state.fhir);

  // PDF Modal state
  const [pdfModalOpened, { open: openPdfModal, close: closePdfModal }] =
    useDisclosure(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [, setPdfError] = useState<string>("");
  const processingRef = useRef(false);
  const [htmlData, setHtmlData] = useState<any>("");

  // Data states
  const [patientQueryData, setPatientQueryData] = useState<PatientQueryData[]>(
    []
  );
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // Updated filter states - Using custom status filter
  const [customStatusFilter, setCustomStatusFilter] = useState<string[]>([
    "open_and_in_progress", // Changed from ["open,in_progress"]
  ]);

  const [topicFilter, setTopicFilter] = useState<string[]>([]);
  const [queryTypeFilter, setQueryTypeFilter] = useState<string[]>(["all"]);
  const [search, setSearch] = useState<string>("");
  const [rowStatus, setRowStatus] = useState<any>();
  const [rowID, setRowID] = useState<any>();
  const [initialLoading, setInitialLoading] = useState(true);
  const [smartFhirLoading, setSmartFhirLoading] = useState(true);
  const [downloadingRowId, setDownloadingRowId] = useState<string | null>(null);
  const [printingRowId, setPrintingRowId] = useState<string | null>(null);
  const [rowClickLoading, setRowClickLoading] = useState(false);

  // Error states - consolidated
  const [errors, setErrors] = useState({
    patientQuery: null as string | null,
    status: null as string | null,
    topic: null as string | null,
    smartFhir: null as string | null,
    mrn: null as string | null,
    practitioner: null as string | null,
    encounter: null as string | null,
  });

  // Updated options for dropdowns
  const [originalStatusOptions, setOriginalStatusOptions] = useState<any[]>([]);
  const [customStatusOptions, setCustomStatusOptions] = useState<any[]>([]);
  const [statusMapping, setStatusMapping] = useState<any>({});
  const [topicOptions, setTopicOptions] = useState<any[]>([]);
  const [modalDownloading, setModalDownloading] = useState(false);
  const [modalPrint, setModalPrint] = useState(false);
  // Track if filters are initialized
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  // FHIR Data states
  const [fhirClient, setFhirClient] = useState<any>(null);
  const [patientData, setPatientData] = useState<any>(null);
  const [practitionerData, setPractitionerData] =
    useState<FhirPractitioner | null>(null);

  // Static options for query type dropdown
  const queryTypeOptions = [{ value: "all", label: "time 1" }];

  // Utility function to update specific error
  const updateError = useCallback((key: keyof typeof errors, value: string | null) => {
    setErrors((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Utility function to download PDF
  const downloadPdf = async (blob: Blob, filename?: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "document.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  // Utility function to check if running in Epic Hyperspace
  const isEpicHyperspace = (): boolean => {
    return !!(
      (window as any).Epic ||
      (window as any).EpicWeb ||
      window.self !== window.top ||
      window.location.href.includes("epic") ||
      window.location.href.includes("hyperspace")
    );
  };
  const fetchHtmlFromArcPlus = async (id: string): Promise<any | null> => {
    try {
      const response = await fetchHtmlUrlQuery(id);
      return response?.data || null;
    } catch (error) {
      console.error("Failed to fetch PDF URL:", error);
      return null;
    }
  };

  // Utility function to open PDF in external window/browser
  const openPdfInExternalWindow = (blob: Blob, filename?: string) => {
    const url = URL.createObjectURL(blob);

    try {
      if (isEpicHyperspace()) {
        try {
          if (
            (window as any).Epic?.Clinical?.Informatics?.Web?.OpenExternalWindow
          ) {
            (window as any).Epic.Clinical.Informatics.Web.OpenExternalWindow(
              url
            );
            setTimeout(() => URL.revokeObjectURL(url), 10000);
            return;
          }
        } catch (error) {
          console.warn(
            "Epic OpenExternalWindow method failed, fallback to window.open:",
            error
          );
        }
      }

      const externalWindow = window.open(
        url,
        "_blank",
        "width=1024,height=768,scrollbars=yes,resizable=yes"
      );

      if (!externalWindow) {
        // Pop-up blocked → just alert or notify user
        console.error("Pop-up blocked or failed to open external window");
      }

      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      console.error("Failed to open PDF in external window:", error);
      alert("Failed to open PDF. Please try again.");
      URL.revokeObjectURL(url);
    }
  };

  // Fetch PDF from ArcPlus service
  const fetchPdfUrlFromArcPlus = async (
    id: string
  ): Promise<PdfResponse | null> => {
    try {
      const response = await fetchPdfUrlQuery(id);
      return response?.data || null;
    } catch (error) {
      console.error("Failed to fetch PDF URL:", error);
      return null;
    }
  };

  // Enhanced MRN validation and extraction
  const validateAndExtractMrn = useCallback(
    (patientData: any): { mrn: string | null; error: string | null } => {
      try {
        if (!patientData) {
          return { mrn: null, error: "Patient data is not available" };
        }

        if (!patientData.identifier || !Array.isArray(patientData.identifier)) {
          return {
            mrn: null,
            error: "Patient identifiers are missing or invalid",
          };
        }

        // First, try to find MRN identifier
        const mrnIdentifier = patientData.identifier.find(
          (identifier: any) => identifier.type?.text === "MRN"
        );

        if (
          mrnIdentifier &&
          mrnIdentifier.value &&
          typeof mrnIdentifier.value === "string"
        ) {
          const mrnValue = mrnIdentifier.value.trim();
          if (mrnValue.length > 0) {
            return { mrn: mrnValue, error: null };
          }
        }

        // If MRN not found or invalid, search for EPI identifier
        const epiIdentifier = patientData.identifier.find(
          (identifier: any) => identifier.type?.text === "EPI"
        );

        if (!epiIdentifier) {
          return {
            mrn: null,
            error: "Neither MRN nor EPI identifier found in patient data",
          };
        }

        if (!epiIdentifier.value || typeof epiIdentifier.value !== "string") {
          return { mrn: null, error: "EPI value is missing or invalid" };
        }

        const epiValue = epiIdentifier.value.trim();
        if (epiValue.length === 0) {
          return { mrn: null, error: "EPI value is empty" };
        }

        return { mrn: epiValue, error: null };
      } catch (error) {
        console.error("Error extracting MRN/EPI:", error);
        return {
          mrn: null,
          error: "Failed to extract MRN or EPI from patient data",
        };
      }
    },
    []
  );

  const getPractitionerNPI = useCallback(
    (practitioner: FhirPractitioner | null): string => {
      if (!practitioner?.identifier || practitioner.identifier.length === 0) {
        return "";
      }

      const npiIdentifier = practitioner.identifier.find(
        (id) => id.type?.text === "NPI"
      );

      if (npiIdentifier?.value) {
        return npiIdentifier.value;
      }

      const externalIdentifier = practitioner.identifier.find(
        (id) =>
          id.type?.text === "EXTERNAL" &&
          id.value &&
          /^[0-9]+$/.test(id.value.trim())
      );

      return externalIdentifier?.value?.trim() || "";
    },
    []
  );

  // Fetch practitioner data from FHIR client
  const fetchPractitionerData = useCallback(async (client: any) => {
    try {
      updateError("practitioner", null);

      // Check if user information is available
      if (!client.user) {
        console.warn("No user information available from FHIR client");
        updateError(
          "practitioner",
          "No user information available in FHIR client"
        );
        return null;
      }

      let practitioner: FhirPractitioner | null = null;

      // Try to get practitioner from fhirUser reference
      if (client.user.fhirUser) {
        try {
          console.log(
            "Fetching practitioner from fhirUser:",
            client.user.fhirUser
          );
          practitioner = await client.request(client.user.fhirUser);
        } catch (error) {
          console.error("Error fetching practitioner from fhirUser:", error);
        }
      }

      // If fhirUser didn't work, try using user ID to search for practitioner
      if (!practitioner && client.user.id) {
        try {
          console.log("Searching for practitioner by user ID:", client.user.id);
          const searchResult = await client.request(
            `Practitioner?identifier=${client.user.id}`
          );

          if (searchResult?.entry && searchResult.entry.length > 0) {
            practitioner = searchResult.entry[0].resource;
          }
        } catch (error) {
          console.error("Error searching practitioner by user ID:", error);
        }
      }

      // Try searching by user profile information if available
      if (!practitioner && client.user.profile) {
        try {
          const profile = client.user.profile;
          let searchParams = [];

          if (profile.family_name && profile.given_name) {
            searchParams.push(
              `family=${encodeURIComponent(profile.family_name)}`
            );
            searchParams.push(
              `given=${encodeURIComponent(profile.given_name)}`
            );
          } else if (profile.name) {
            searchParams.push(`name=${encodeURIComponent(profile.name)}`);
          }

          if (searchParams.length > 0) {
            const searchQuery = `Practitioner?${searchParams.join("&")}`;
            console.log("Searching practitioner by profile:", searchQuery);

            const searchResult = await client.request(searchQuery);
            if (searchResult?.entry && searchResult.entry.length > 0) {
              practitioner = searchResult.entry[0].resource;
            }
          }
        } catch (error) {
          console.error("Error searching practitioner by profile:", error);
        }
      }

      if (practitioner) {
        console.log("Practitioner data fetched successfully:", practitioner);
        setPractitionerData(practitioner);

        // Extract and set NPI in Redux
        const npi = getPractitionerNPI(practitioner);
        if (npi) {
          dispatch(setProviderNPI(npi));
          console.log("Provider NPI set in Redux:", npi);
        }

        return practitioner;
      } else {
        console.warn("No practitioner data found");
        updateError(
          "practitioner",
          "Could not retrieve practitioner information from FHIR server"
        );
        return null;
      }
    } catch (error) {
      console.error("Error fetching practitioner data:", error);
      updateError(
        "practitioner",
        "Failed to fetch practitioner data from FHIR server"
      );
      return null;
    }
  }, [dispatch, getPractitionerNPI, updateError]);
  const handleDownloadModal = async (row: string) => {
    try {
      setModalDownloading(true);
      const response = await fetchPdfUrlFromArcPlus(row);

      if (!response) {
        console.error("No PDF response received for download");
        return;
      }

      const blobData = response?.blobData;
      const contentType = response?.contentType || "application/pdf";
      const filename = response?.filename || `patient_query_${row}.pdf`;

      let actualBlob: Blob;

      if (blobData instanceof Blob) {
        actualBlob = blobData;
      } else if (typeof blobData === "string") {
        try {
          const stringData = blobData as string;
          const base64Data = stringData.includes(",")
            ? stringData.split(",")[1]
            : stringData;

          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);

          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          actualBlob = new Blob([bytes], { type: contentType });
        } catch (error) {
          console.error(
            "Failed to convert base64 to blob for download:",
            error
          );
          return;
        }
      } else {
        console.error("Invalid PDF data format for download");
        return;
      }

      if (actualBlob.size === 0) {
        console.error("Received empty PDF file for download");
        return;
      }
      await downloadPdf(actualBlob, filename);
      openPdfInExternalWindow(actualBlob, filename);

      setModalDownloading(false);
    } catch (error) {
      console.error("Error handling PDF download:", error);
    } finally {
      setDownloadingRowId(null);
      setModalDownloading(false);
    }
  };
  const handlePrintMOdal = async (row: string) => {
    try {
      setModalPrint(true);

      const response = await fetchPdfUrlFromArcPlus(row);

      if (!response) {
        console.error("No PDF response received for print");
        return;
      }

      const blobData = response?.blobData;
      const contentType = response?.contentType || "application/pdf";

      let actualBlob: Blob;

      if (blobData instanceof Blob) {
        actualBlob = blobData;
      } else if (typeof blobData === "string") {
        try {
          const stringData = blobData as string;
          const base64Data = stringData.includes(",")
            ? stringData.split(",")[1]
            : stringData;

          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);

          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          actualBlob = new Blob([bytes], { type: contentType });
        } catch (error) {
          console.error("Failed to convert base64 to blob for print:", error);
          return;
        }
      } else {
        console.error("Invalid PDF data format for print");
        return;
      }

      if (actualBlob.size === 0) {
        console.error("Received empty PDF file for print");
        return;
      }

      openPdfInExternalWindow(actualBlob);
      setModalPrint(false);
    } catch (error) {
      console.error("Error handling PDF print:", error);
    } finally {
      setPrintingRowId(null);
      setModalPrint(false);
    }
  };

  const isTokenAvailable = useCallback(() => {
    const token = localStorage.getItem(STORAGE_KEYS.JWT_TOKEN);
    return Boolean(
      token && token !== "" && token !== "null" && token !== "undefined"
    );
  }, []);

  const referenceDataEnabled = filtersInitialized && isTokenAvailable();

  const { data: statusResponse, isLoading: statusQueryLoading } = useStatusList(
    referenceDataEnabled
  );
  const { data: topicResponse, isLoading: topicQueryLoading } = useTopicList(
    referenceDataEnabled
  );

  useEffect(() => {
    if (statusResponse?.data) {
      const originalOptions = convertToSelectOptions(statusResponse.data);
      setOriginalStatusOptions(originalOptions);
      const { customStatusOptions: nextCustomOptions, statusMapping: mapping } =
        createCustomStatusOptions(originalOptions);
      setCustomStatusOptions(nextCustomOptions);
      setStatusMapping(mapping);
      setCustomStatusFilter(["open_and_in_progress"]);
      updateError("status", null);
    }
  }, [statusResponse, updateError]);

  useEffect(() => {
    if (topicResponse?.data) {
      const options = convertToSelectOptions(topicResponse.data);
      const allTopicValues = options?.map((option: any) => option?.value);
      setTopicFilter(allTopicValues);
      setTopicOptions(options);
      updateError("topic", null);
    }
  }, [topicResponse, updateError]);

  const practitionerNPI = useMemo(
    () => getPractitionerNPI(practitionerData),
    [practitionerData, getPractitionerNPI]
  );

  const mrnNumber = useMemo(() => {
    const { mrn } = validateAndExtractMrn(patientData);
    return mrn ?? "";
  }, [patientData, validateAndExtractMrn]);

  const queryListParams = useMemo(() => {
    const actualStatusFilter = convertCustomStatusToActual(
      customStatusFilter,
      statusMapping
    );

    return {
      status_id:
        actualStatusFilter?.length > 0 ? actualStatusFilter.join(",") : "",
      topic_id: topicFilter?.length > 0 ? topicFilter.join(",") : "",
      query_type: queryTypeFilter?.length > 0 ? queryTypeFilter[0] : "",
      mrn_no: mrnNumber,
      provider_npi: practitionerNPI,
      filter_id: "",
      start_date: "",
      end_date: "",
      limit,
      page: page - 1,
      search,
    };
  }, [
    customStatusFilter,
    mrnNumber,
    page,
    practitionerNPI,
    queryTypeFilter,
    search,
    statusMapping,
    topicFilter,
  ]);

  const queriesEnabled =
    referenceDataEnabled &&
    !initialLoading &&
    !smartFhirLoading &&
    Boolean(patientData) &&
    Boolean(mrnNumber);

  const {
    data: queryListResponse,
    isLoading: patientQueryLoadingQuery,
    isFetching,
    refetch: refetchQueryList,
  } = useQueryList(queryListParams, {
    enabled: queriesEnabled,
    polling: true,
  });

  const patientQueryLoading =
    patientQueryLoadingQuery || statusQueryLoading || topicQueryLoading;

  useEffect(() => {
    if (!queriesEnabled) return;

    if (queryListResponse?.data?.data) {
      setTotalPages(Math.ceil(queryListResponse.data.total_count / limit));
      const transformedData = transformApiDataToPatientQuery(
        queryListResponse.data,
        originalStatusOptions,
        provider?.timezone_info
      );
      setPatientQueryData(transformedData);
      updateError("patientQuery", null);
      updateError("mrn", null);
    } else if (queryListResponse && !queryListResponse.data?.data) {
      setPatientQueryData([]);
      setTotalPages(0);
      updateError(
        "patientQuery",
        "No patient queries found for the current filters."
      );
    }
  }, [
    originalStatusOptions,
    provider?.timezone_info,
    queriesEnabled,
    queryListResponse,
    updateError,
  ]);

  const isRefreshing =
    isFetching && !patientQueryLoadingQuery && !initialLoading && !smartFhirLoading;

  useEffect(() => {
    if (!filtersInitialized) return;
    setPage(1);
  }, [customStatusFilter, topicFilter, queryTypeFilter, search, filtersInitialized]);

  // Helper function to extract patient ID from various FHIR response formats
  const getPatientId = useCallback((patientData: any): string | null => {
    try {
      // Direct patient resource
      if (patientData?.id) {
        return patientData.id;
      }

      // CombinedFetchResult format
      if (patientData?.data?.id) {
        return patientData.data.id;
      }

      // Bundle entry format
      if (patientData?.entry?.[0]?.resource?.id) {
        return patientData.entry[0].resource.id;
      }

      // Resource property
      if (patientData?.resource?.id) {
        return patientData.resource.id;
      }

      console.warn("Could not extract patient ID from:", patientData);
      return null;
    } catch (error) {
      console.error("Error extracting patient ID:", error);
      return null;
    }
  }, []);

  // Helper function to extract patient resource from various FHIR response formats
  const getPatientResource = useCallback((patientData: any): any => {
    try {
      // Direct patient resource
      if (patientData?.resourceType === "Patient") {
        return patientData;
      }

      // CombinedFetchResult format
      if (patientData?.data?.resourceType === "Patient") {
        return patientData.data;
      }

      // Bundle entry format
      if (patientData?.entry?.[0]?.resource?.resourceType === "Patient") {
        return patientData.entry[0].resource;
      }

      // Resource property
      if (patientData?.resource?.resourceType === "Patient") {
        return patientData.resource;
      }

      // If it's already a patient resource but doesn't have resourceType
      if (patientData?.id && (patientData?.name || patientData?.identifier)) {
        return patientData;
      }

      console.warn("Could not extract patient resource from:", patientData);
      return patientData; // Return as-is if we can't determine the format
    } catch (error) {
      console.error("Error extracting patient resource:", error);
      return patientData;
    }
  }, []);

  // Initialize SMART on FHIR client and load data
  useEffect(() => {
    const initializeApp = async () => {
      try {
        if (fhirState.isInitialized) {
          const existingClient = getHeldFhirClient();
          if (existingClient) {
            setFhirClient(existingClient);
          }
          setPatientData(fhirState.patient);
          setPractitionerData(fhirState.practitioner);
          setSmartFhirLoading(false);

          const token = localStorage.getItem(STORAGE_KEYS.JWT_TOKEN);
          const hasToken =
            token && token !== "" && token !== "null" && token !== "undefined";

          if (hasToken) {
            try {
              if (!localStorage.getItem(STORAGE_KEYS.KEY_ID)) {
                await initializeEncryptionIfNeeded();
              }
            } catch (apiError) {
              console.error("Error fetching initial data:", apiError);
            }
          }

          setFiltersInitialized(true);
          setInitialLoading(false);
          return;
        }

        setSmartFhirLoading(true);
        updateError("smartFhir", null);
        dispatch(setFhirInitializing());

        try {
          console.log("Initializing SMART on FHIR client...");
          const client = await FHIR.oauth2.ready();
          console.log("FHIR client ready:", client);

          const accessToken = client?.state?.tokenResponse?.access_token;
          const idToken = client?.state?.tokenResponse?.id_token;
          const baseUrl = client?.state?.serverUrl;

          // Step 1: Handle token exchange first
          let tokenExchangeSuccess = false;
          if (accessToken && idToken && baseUrl) {
            try {
              const apiBaseUrl = process.env.REACT_APP_BASE_URL;
              if (!localStorage.getItem(STORAGE_KEYS.JWT_TOKEN)) {
                const tokenExchangeUrl = `${apiBaseUrl}crypto/auth/token-exchange`;

                console.log(
                  "Making token exchange request to:",
                  tokenExchangeUrl
                );

                const tokenExchangeResponse = await fetch(tokenExchangeUrl, {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    "id-token": idToken,
                    "access-token": accessToken,
                    "base-url": baseUrl,
                  },
                });

                if (tokenExchangeResponse.ok) {
                  const exchangeResult = await tokenExchangeResponse.json();
                  if (exchangeResult.data) {
                    localStorage.setItem(
                      STORAGE_KEYS.JWT_TOKEN,
                      exchangeResult.data.access_token || ""
                    );
                    localStorage.setItem(
                      STORAGE_KEYS.EPIC_INTROSPECT_URL,
                      baseUrl || ""
                    );
                    // Store refresh token if available
                    if (exchangeResult.data.refresh_token) {
                      localStorage.setItem(
                        STORAGE_KEYS.REFRESH_TOKEN,
                        exchangeResult.data.refresh_token
                      );
                    }
                    const now = Date.now();

                    const accessExpiresInSec =
                      exchangeResult.data.expires_in || 7200;

                    // const accessExpiresInSec = 5 * 60; // test 5 mins
                    const refreshExpiresInSec =
                      exchangeResult.data.refresh_expires_in || 604800; // default 7 days

                    localStorage.setItem(
                      STORAGE_KEYS.EXPIRES_IN,
                      String(now + accessExpiresInSec * 1000)
                    );
                    localStorage.setItem(
                      STORAGE_KEYS.REFRESH_EXPIRES_IN,
                      String(now + refreshExpiresInSec * 1000)
                    );

                    console.log("Token exchange successful, new token stored");
                    tokenExchangeSuccess = true;
                  } else {
                    throw new Error(
                      "Invalid response structure from token exchange"
                    );
                  }
                } else {
                  const errorText = await tokenExchangeResponse.text();
                  console.error("Token exchange failed:", errorText);
                  throw new Error(`Token exchange failed: ${errorText}`);
                }
              } else tokenExchangeSuccess = true;
            } catch (tokenError) {
              console.error("Token exchange error:", tokenError);
              // Continue with FHIR operations but note the token issue
            }
          } else {
            console.warn("Missing tokens or base URL for token exchange");
          }

          // Step 2: Fetch patient data (this doesn't require our custom token)
          const rawPatientData = await client.patient.read();
          console.log("Raw patient data:", rawPatientData);

          // Extract the actual patient resource and ID
          const patientResource = getPatientResource(rawPatientData);
          const patientId = getPatientId(rawPatientData);

          console.log("Extracted patient resource:", patientResource);
          console.log("Extracted patient ID:", patientId);

          if (!patientId) {
            throw new Error("Could not extract patient ID from FHIR response");
          }

          setFhirClient(client);
          holdFhirClient(client);
          setPatientData(patientResource);

          // Step 3: Fetch practitioner data
          const practitioner = await fetchPractitionerData(client);

          dispatch(
            setFHIRPatientAndPractitioner({
              practitioner,
              patient: patientResource,
              encounter: client?.encounter?.id,
            })
          );

          // Step 4: Only proceed if token exchange was successful
          if (tokenExchangeSuccess) {
            console.log("Token available, reference data will load via react-query");
          } else {
            console.warn("Token exchange failed - stopping initialization");
            setSmartFhirLoading(false);
            return; // Stop here if no token
          }
          // Extract MRN and set in Redux
          const { mrn: extractedMrn } = validateAndExtractMrn(patientResource);
          console.log("MRN set to Redux :-", extractedMrn);
          dispatch(setFhirMrn(extractedMrn));

          // Mark SMART FHIR loading as complete only after everything is done
          setSmartFhirLoading(false);
          dispatch(setFhirInitialized({ mrn: extractedMrn }));
        } catch (smartError) {
          console.error("Error initializing SMART on FHIR:", smartError);
          const errorMessage =
            "Failed to initialize SMART on FHIR client. Please ensure you're accessing this from a compatible EHR system.";
          releaseFhirClient();
          updateError("smartFhir", errorMessage);
          setSmartFhirLoading(false);
          dispatch(setFhirFailed(errorMessage));
        }
      } catch (error) {
        console.error("Error during initialization:", error);
        const errorMessage =
          "Failed to initialize application. Please try again.";
        releaseFhirClient();
        dispatch(setFhirFailed(errorMessage));
      } finally {
        setFiltersInitialized(true);
        setInitialLoading(false);
      }
    };

    initializeApp();
  }, [
    dispatch,
    fetchPractitionerData,
    getPatientId,
    getPatientResource,
    fhirState.isInitialized,
    fhirState.patient,
    fhirState.practitioner,
    updateError,
    validateAndExtractMrn,
  ]);

  // Cleanup PDF blob when modal closes
  useEffect(() => {
    return () => {
      if (pdfBlob) {
        URL.revokeObjectURL(URL.createObjectURL(pdfBlob));
      }
    };
  }, [pdfBlob]);

  // Retry functions
  const retryFhirConnection = () => {
    window.location.reload();
  };

  const retryDataFetch = () => {
    if (errors.status) {
      void queryClient.invalidateQueries({ queryKey: ["statusList"] });
    }
    if (errors.topic) {
      void queryClient.invalidateQueries({ queryKey: ["topicList"] });
    }
    if (errors.patientQuery || errors.mrn) {
      void refetchQueryList();
    }
    if (errors.practitioner && fhirClient) fetchPractitionerData(fhirClient);
  };

  // Event handlers
  const handleEdit = (row: PatientQueryData) => {
    navigate(RouteConstants.ROUTE_QUERY_DETAILS.replace(":query_id", row?.id), {
      state: {
        queryData: row,
        editMode: true,
        from: RouteConstants.ROUTE_CDI_MODULE,
      },
    });
  };

  const handleDownload = async (row: PatientQueryData) => {
    try {
      setDownloadingRowId(row.id);

      const response = await fetchPdfUrlFromArcPlus(row?.id);

      if (!response) {
        console.error("No PDF response received for download");
        return;
      }

      const blobData = response?.blobData;
      const filename = response?.filename || `patient_query_${row.id}.pdf`;
      const contentType = response?.contentType || "application/pdf";

      let actualBlob: Blob;

      if (blobData instanceof Blob) {
        actualBlob = blobData;
      } else if (typeof blobData === "string") {
        try {
          const stringData = blobData as string;
          const base64Data = stringData.includes(",")
            ? stringData.split(",")[1]
            : stringData;

          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);

          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          actualBlob = new Blob([bytes], { type: contentType });
        } catch (error) {
          console.error(
            "Failed to convert base64 to blob for download:",
            error
          );
          return;
        }
      } else {
        console.error("Invalid PDF data format for download");
        return;
      }

      if (actualBlob.size === 0) {
        console.error("Received empty PDF file for download");
        return;
      }
      await downloadPdf(actualBlob, filename);
      openPdfInExternalWindow(actualBlob, filename);
    } catch (error) {
      console.error("Error handling PDF download:", error);
    } finally {
      setDownloadingRowId(null);
    }
  };

  const handlePrint = async (row: PatientQueryData) => {
    try {
      setPrintingRowId(row.id);

      console.log("Starting print process for query:", row.id);
      const response = await fetchPdfUrlFromArcPlus(row?.id);

      if (!response) {
        console.error("No PDF response received for print");
        return;
      }

      const blobData = response?.blobData;
      const filename = response?.filename || `patient_query_${row.id}.pdf`;
      const contentType = response?.contentType || "application/pdf";

      if (!blobData) {
        console.error("No blob data in response");
        return;
      }

      let actualBlob: Blob;

      if (blobData instanceof Blob) {
        actualBlob = blobData;
      } else if (typeof blobData === "string") {
        try {
          const stringData = blobData as string;
          const base64Data = stringData.includes(",")
            ? stringData.split(",")[1]
            : stringData;

          if (!base64Data) {
            throw new Error("No base64 data found");
          }

          const binaryString = atob(base64Data);
          const bytes = new Uint8Array(binaryString.length);

          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }

          actualBlob = new Blob([bytes], { type: contentType });
        } catch (conversionError) {
          console.error(
            "Failed to convert base64 to blob for print:",
            conversionError
          );
          return;
        }
      } else {
        console.error("Invalid PDF data format for print:", typeof blobData);
        return;
      }

      if (actualBlob.size === 0) {
        console.error("Received empty PDF file for print");
        return;
      }

      console.log("PDF blob created successfully, size:", actualBlob.size);
      openPdfInExternalWindow(actualBlob, filename);
    } catch (error) {
      console.error("Error in print handler:", error);
    } finally {
      setPrintingRowId(null);
    }
  };

  const handleRowClick = async (row: PatientQueryData) => {
    if (processingRef.current) {
      return;
    }

    if (processingRef.current) {
      console.log("BLOCKED: Already processing");
      return;
    }
    if (querySubmittedStates?.includes(Number(row?.statusId))) {
      try {
        setRowClickLoading(true);
        setPdfLoading(true);
        processingRef.current = true;

        setPdfError("");
        const responseHtml = await fetchHtmlFromArcPlus(row?.id);
        if (!responseHtml) {
          setPdfError("No HTML content received");
          return;
        }
        setHtmlData(responseHtml?.data);

        setRowStatus(row?.status);
        setRowID(row?.id);

        openPdfModal();
      } catch (error) {
        console.error("Error handling PDF preview:", error);
        setPdfError("An error occurred while processing the PDF");
      } finally {
        setPdfLoading(false);
        setRowClickLoading(false);
        processingRef.current = false;
      }
    } else {
      navigate(
        RouteConstants.ROUTE_QUERY_DETAILS.replace(":query_id", row?.id),
        {
          state: { queryData: row, from: RouteConstants.ROUTE_CDI_MODULE },
        }
      );
    }
  };

  const handlePdfModalClose = () => {
    closePdfModal();
    setPdfBlob(null);
    setPdfError("");
  };

  const handlePdfError = (error: string) => {
    setPdfError(error);
  };

  // Column definitions for the patient query table
  const patientQueryColumns = [
    {
      header: "Topic",
      accessor: "topic" as keyof PatientQueryData,
      align: "left" as const,
      minWidth: "120px",
    },
    {
      header: "Status",
      accessor: "status" as keyof PatientQueryData,
      align: "left" as const,
      cellType: "status" as const,
      minWidth: "100px",
    },
    {
      header: "Ageing",
      accessor: "ageing" as keyof PatientQueryData,
      align: "left" as const,
      cellType: "aging" as const,
      minWidth: "120px",
    },
    {
      header: "Query By / Date",
      align: "left" as const,
      accessor: (row: PatientQueryData) => (
        <Tooltip
          arrowPosition='side'
          arrowOffset={5}
          arrowSize={5}
          arrowRadius={3}
          label={
            <div style={{ textAlign: "left" }}>
              <div> {row.queryBy}</div>
              <div>{`${row?.queryDate}, ${row?.createdFormatTime}`}</div>
              <div>{`${row?.id}`}</div>
            </div>
          }
          position='bottom-start'
          withArrow
          multiline
        >
          <div>
            <Text size={isSmallScreen ? "12px" : "14px"}>{row.queryBy}</Text>
            <Text size='xs' mt={"2px"} c='dimmed'>
              {row.queryDate}
            </Text>
          </div>
        </Tooltip>
      ),
      minWidth: "150px",
    },
  ];

  // Error UI Component
  const ErrorCard = ({
    title,
    message,
    icon,
    onRetry,
    type = "warning",
  }: {
    title: string;
    message: string;
    icon: React.ReactNode;
    onRetry?: () => void;
    type?: "error" | "warning" | "info";
  }) => (
    <Card withBorder p='lg' mb='md'>
      <Group align='flex-start' gap='md'>
        <ThemeIcon
          size='lg'
          variant='light'
          color={
            type === "error" ? "red" : type === "warning" ? "orange" : "blue"
          }
        >
          {icon}
        </ThemeIcon>
        <Stack gap='xs' style={{ flex: 1 }}>
          <Text
            fw={600}
            size='sm'
            c={
              type === "error" ? "red" : type === "warning" ? "orange" : "blue"
            }
          >
            {title}
          </Text>
          <Text size='sm' c='dimmed'>
            {message}
          </Text>
          {onRetry && (
            <Group gap='xs' mt='xs'>
              <Button
                size='xs'
                variant='light'
                leftSection={<IconRefresh size={14} />}
                onClick={onRetry}
              >
                Retry
              </Button>
            </Group>
          )}
        </Stack>
      </Group>
    </Card>
  );

  if (smartFhirLoading) {
    return <SmartFhirLoadingScreen />;
  }

  // Check for critical errors that prevent the app from functioning
  const hasCriticalError = errors.smartFhir && !patientData;

  if (hasCriticalError) {
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <Box style={{ maxWidth: "600px", width: "100%" }}>
          <ErrorCard
            title='SMART on FHIR Connection Failed'
            message={errors.smartFhir!}
            icon={<IconWifi size={20} />}
            onRetry={retryFhirConnection}
            type='error'
          />
        </Box>
      </div>
    );
  }

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* PDF Preview Modal with PDF.js Viewer */}
      <LoadingOverlay
        visible={rowClickLoading}
        zIndex={2000}
        overlayProps={{ radius: "sm", blur: 0.2 }}
        loaderProps={{ size: "lg" }}
      />
      <Modal
        opened={pdfModalOpened}
        onClose={handlePdfModalClose}
        size='xl'
        centered
        styles={{
          body: { padding: 0 },
          header: { borderBottom: "1px solid #e9ecef" },
        }}
        withCloseButton={false}
      >
        <div style={{ height: "80vh" }}>
          <LoadingOverlay visible={pdfLoading} />
          <HtmlViewer
            htmlContent={htmlData}
            onError={handlePdfError}
            queryId={rowID}
            status={rowStatus}
            onClose={handlePdfModalClose}
            onDownload={() => handleDownloadModal(rowID)}
            onPrint={() => handlePrintMOdal(rowID)}
            downloadLoading={modalDownloading}
            printLoading={modalPrint}
          />
        </div>
      </Modal>

      {(errors.status || errors.topic) && (
        <ErrorCard
          title='Data Loading Issue'
          message={`${errors.status ? "Status data failed to load. " : ""}${
            errors.topic ? "Topic data failed to load." : ""
          }`}
          icon={<IconDatabase size={16} />}
          onRetry={retryDataFetch}
          type='warning'
        />
      )}

      {errors.patientQuery && !errors.mrn && (
        <ErrorCard
          title='Query Loading Issue'
          message={errors.patientQuery}
          icon={<IconAlertCircle size={16} />}
          onRetry={retryDataFetch}
          type='warning'
        />
      )}
      {/* </Box>  */}
      <LoadingOverlay
        visible={rowClickLoading}
        zIndex={2000}
        overlayProps={{ radius: "sm", blur: 0.2 }}
        loaderProps={{ size: "lg" }}
      />

      <Box style={{ flexShrink: 0 }}>
        <FilterComponent
          loading={patientQueryLoading}
          refreshing={isRefreshing}
          dropDown1Options={customStatusOptions}
          dropDown2Options={topicOptions}
          dropDown3Options={queryTypeOptions}
          dropdown1Values={customStatusFilter}
          setDropdown1Values={setCustomStatusFilter}
          dropdown2Values={topicFilter}
          setDropdown2Values={setTopicFilter}
          dropdown3Values={queryTypeFilter}
          setDropdown3Values={setQueryTypeFilter}
          searchValue={search}
          setSearchValue={setSearch}
          debounceDelay={500}
        />
      </Box>

      <ReusableTable
        showRowIdBelowActions={true}
        showSeriesNumber
        loading={patientQueryLoading}
        data={patientQueryData}
        downloadingLoading={downloadingRowId}
        printingLoading={printingRowId}
        columns={patientQueryColumns}
        onEdit={handleEdit}
        onDownload={handleDownload}
        onPrint={handlePrint}
        onRowClick={handleRowClick}
        statusColors={statusColors}
        canEdit={(row) => row.status === "Completed" && row?.isEditable === "Y"}
      />

      <Box
        style={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "flex-end",
          padding: "8px 4px",
        }}
      >
        <PaginationComponent
          total={totalPages}
          value={page}
          onChange={setPage}
          disabled={patientQueryLoading || !!errors.mrn}
        />
      </Box>
    </div>
  );
}
